import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import Keyboard from 'containers/Keyboard';
import qs from 'querystring';
import { LastFocusKeeper } from 'components/HOCs/withKeepingLastFocus';
import useContentNavigation from 'hooks/useContentNavigation';
import useLeaveFor from 'hooks/useLeaveFor';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import { INIT_UI_LANGUAGE } from 'constants/languages';
import { SearchResultArea } from './SearchResultArea';
import { KeyboardWrapper, SearchWrapper } from './StyledComponents';
import { isMinQueryLengthExceeded } from './utils';

const SECTION_NAME = 'SEARCH_RESULTS_AREA';
const MAX_QUERY_SIZE = 30;
const LAST_FOCUS_KEY = 'SEARCH';

const KEYBOARD_CONFIG = {
  straightOnly: false,
  straightOverlapThreshold: 0.1,
  leaveFor: {
    up: '@SEARCH_RESULTS_AREA', // This transition direction is controlled by SearchResultArea component
    left: '@MAIN_MENU',
  },
};

const Search = (props) => {
  const { initialQuery, isLoading, results } = props;
  const { fetchSearch, resetSearchQuery, lastFocusKeeper, history } = props;

  const locationParams = useMemo(
    () => qs.parse(decodeURIComponent(history.location.search).replace('?', '')),
    [history],
  );

  const queryFromLocation = !isEmpty(locationParams) ? locationParams.query : initialQuery;
  const [query, setQuery] = useState(queryFromLocation);
  const onContentEnter = useContentNavigation();

  const onListItemEnter = useCallback(
    (item, index) => {
      const elementTopFocus = `#search-results-item-${index}`;

      if (item.type === 'VIEW_ALL') {
        history.push({ pathname: `/search/all`, search: `?query=${query}` });
      } else {
        const modalFocusBackConfig = { elementTopFocus };
        const analyticsParams = {
          eventGroup: 'search',
          eventName: `click`,
          eventValue: `search request="${query}";search result=yes;numberEl=${index + 1};`,
        };
        onContentEnter(item, modalFocusBackConfig, analyticsParams);
      }
      lastFocusKeeper.save(LAST_FOCUS_KEY, elementTopFocus);
    },
    [lastFocusKeeper, history, query, onContentEnter],
  );

  const onCategoryEnter = useCallback(
    (id) => {
      history.push({ pathname: `/search/all`, search: `?query=${query}&category=${id}` });
    },
    [history, query],
  );

  const onQueryUpdated = useCallback(
    (query) => {
      setQuery(query);
      if (isMinQueryLengthExceeded(query)) fetchSearch(query);
    },
    [setQuery, fetchSearch],
  );

  useLeaveFor(history, ({ pathname }) => {
    const isGoingDeep = ['details', 'player', 'search'].some((path) => pathname.includes(path));

    if (isGoingDeep) return;

    const searchResults = isEmpty(get(results, ['content'])) ? 'no' : 'yes';

    resetSearchQuery();

    innerAnalyticsVolia.postEvent({
      eventGroup: 'search',
      eventName: `leave`,
      eventValue: `search request="${query}";search result=${searchResults}`,
    });
  });

  useEffect(() => {
    const hasNoContent = isMinQueryLengthExceeded(query) && isEmpty(get(results, ['content']));
    if (hasNoContent && !isLoading) {
      innerAnalyticsVolia.postEvent({
        eventGroup: 'search',
        eventName: `empty`,
        eventValue: `search request="${query}";search result=no`,
      });
    }
  }, [query, isLoading, results]);

  useEffect(() => {
    const lastFocus = lastFocusKeeper.select(LAST_FOCUS_KEY);
    if (lastFocus.hasSaved()) lastFocus.apply(lastFocus.reset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEmpty(locationParams)) {
      onQueryUpdated(locationParams.query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationParams]);

  return (
    <SearchWrapper>
      <SearchResultArea
        isLoading={isLoading}
        results={isMinQueryLengthExceeded(query) ? results : null}
        query={query}
        onItemEnter={onListItemEnter}
        onCategoryEnter={onCategoryEnter}
        sectionName={SECTION_NAME}
      />
      <KeyboardWrapper>
        <Keyboard
          onQueryUpdated={onQueryUpdated}
          maxQuerySize={MAX_QUERY_SIZE}
          query={query}
          type="search"
          selectedLanguage={INIT_UI_LANGUAGE}
          config={KEYBOARD_CONFIG}
        />
      </KeyboardWrapper>
    </SearchWrapper>
  );
};

Search.propTypes = {
  initialQuery: PropTypes.string,
  fetchSearch: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  results: PropTypes.shape({
    totalElements: PropTypes.number.isRequired,
    content: PropTypes.array.isRequired,
    previewItems: PropTypes.array.isRequired,
    previewItemsCount: PropTypes.number.isRequired,
  }),
  history: PropTypes.object.isRequired,
  resetSearchQuery: PropTypes.func.isRequired,
  lastFocusKeeper: PropTypes.instanceOf(LastFocusKeeper).isRequired,
};

export default memo(Search);
